/**
 * CommentSection Component
 * Main component for displaying and managing comments on any entity
 */

import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  MessageCircle,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Loader2,
  Lock
} from 'lucide-react'
import toast from 'react-hot-toast'
import { CommentItem } from './CommentItem'
import { CommentForm } from './CommentForm'
import {
  getComments,
  createCommentWithMentions,
  updateComment,
  deleteComment,
  uploadAttachment,
  deleteAttachment,
  subscribeToComments
} from '../../services/comments'
import { useAuth } from '../../lib/auth'
import type { EntityType } from '../../types/comment'

interface CommentSectionProps {
  entityType: EntityType
  entityId: string
  title?: string
  defaultCollapsed?: boolean
  collapseIfEmpty?: boolean  // Auto-collapse if no comments exist
  variant?: 'default' | 'subsection'  // 'subsection' for inline style matching other sections
}

const ENTITY_LABELS: Record<EntityType, string> = {
  assignment: 'uppdraget',
  customer: 'kunden',
  article: 'artikeln',
  subcontractor: 'trädgårdsmästaren'
}

export function CommentSection({
  entityType,
  entityId,
  title = 'Kommentarer',
  defaultCollapsed = false,
  collapseIfEmpty = false,
  variant = 'default'
}: CommentSectionProps) {
  const isSubsection = variant === 'subsection'
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed)
  const [hasInitialized, setHasInitialized] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showInternalOnly, setShowInternalOnly] = useState(false)

  const { profile } = useAuth()

  const userRole = profile?.role as 'admin' | 'subcontractor' || 'subcontractor'
  const userId = profile?.user_id || ''

  // Fetch comment count for collapseIfEmpty feature
  const { data: commentCount, isSuccess: countLoaded } = useQuery({
    queryKey: ['comments-count', entityType, entityId, userRole],
    queryFn: async () => {
      const comments = await getComments(entityType, entityId)
      // For subcontractors, only count public (non-internal) comments
      if (userRole === 'subcontractor') {
        return comments.filter(c => !c.is_internal).length
      }
      return comments.length
    },
    enabled: !!entityId && collapseIfEmpty && !hasInitialized
  })

  // Auto-collapse based on comment count - only after query has loaded
  useEffect(() => {
    if (collapseIfEmpty && !hasInitialized && countLoaded && commentCount !== undefined) {
      setIsCollapsed(commentCount === 0)
      setHasInitialized(true)
    }
  }, [collapseIfEmpty, commentCount, countLoaded, hasInitialized])

  const {
    data: comments = [],
    isLoading,
    refetch
  } = useQuery({
    queryKey: ['comments', entityType, entityId],
    queryFn: () => getComments(entityType, entityId),
    enabled: !!entityId && !isCollapsed
  })

  // Subscribe to real-time updates
  useEffect(() => {
    if (isCollapsed || !entityId) return

    const unsubscribe = subscribeToComments(entityType, entityId, () => {
      refetch()
    })

    return () => {
      unsubscribe()
    }
  }, [entityType, entityId, isCollapsed, refetch])

  // Filter comments
  // Subcontractors should never see internal comments
  const visibleComments = userRole === 'subcontractor'
    ? comments.filter(c => !c.is_internal)
    : comments

  const filteredComments = showInternalOnly
    ? visibleComments.filter(c => c.is_internal)
    : visibleComments

  // Count internal comments (only relevant for admins)
  const internalCount = userRole === 'admin'
    ? comments.filter(c => c.is_internal).length
    : 0

  const handleSubmit = async (content: string, isInternal: boolean, files: File[], mentionedUserIds: string[]) => {
    setIsSubmitting(true)

    try {
      // Create the comment with mentions
      const result = await createCommentWithMentions({
        entity_type: entityType,
        entity_id: entityId,
        content,
        is_internal: isInternal
      }, mentionedUserIds)

      if (!result.success || !result.comment) {
        toast.error(result.error || 'Kunde inte skapa kommentar')
        return
      }

      // Upload attachments if any
      if (files.length > 0) {
        const uploadPromises = files.map(file =>
          uploadAttachment(result.comment!.id, file)
        )

        const uploadResults = await Promise.all(uploadPromises)
        const failedUploads = uploadResults.filter(r => !r.success)

        if (failedUploads.length > 0) {
          toast.error(`${failedUploads.length} av ${files.length} filer kunde inte laddas upp`)
        }
      }

      toast.success('Kommentar skapad')
      refetch()
    } catch (error) {
      console.error('Error creating comment:', error)
      toast.error('Något gick fel')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleUpdate = async (commentId: string, content: string) => {
    const result = await updateComment(commentId, { content })
    if (result.success) {
      toast.success('Kommentar uppdaterad')
      refetch()
    } else {
      toast.error(result.error || 'Kunde inte uppdatera kommentar')
    }
  }

  const handleDelete = async (commentId: string) => {
    if (!confirm('Är du säker på att du vill ta bort denna kommentar?')) return

    const result = await deleteComment(commentId)
    if (result.success) {
      toast.success('Kommentar borttagen')
      refetch()
    } else {
      toast.error(result.error || 'Kunde inte ta bort kommentar')
    }
  }

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!confirm('Är du säker på att du vill ta bort denna bilaga?')) return

    const result = await deleteAttachment(attachmentId)
    if (result.success) {
      toast.success('Bilaga borttagen')
      refetch()
    } else {
      toast.error(result.error || 'Kunde inte ta bort bilaga')
    }
  }

  return (
    <div className={isSubsection ? '' : 'bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700'}>
      {/* Header */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className={isSubsection
          ? 'w-full flex items-center justify-between mb-2'
          : 'w-full px-6 py-4 flex items-center justify-between hover:bg-nature-50 dark:hover:bg-nature-700 transition-colors rounded-t-lg'
        }
      >
        <div className="flex items-center space-x-2">
          <MessageCircle className={isSubsection ? 'h-4 w-4 text-nature-500' : 'h-5 w-5 text-nature-500'} />
          <h3 className={isSubsection
            ? 'text-sm font-semibold text-nature-700 dark:text-nature-300'
            : 'text-lg font-semibold text-nature-900 dark:text-nature-100'
          }>
            {title}
          </h3>
          <span className={isSubsection ? 'text-xs text-nature-500' : 'text-sm text-nature-500'}>
            ({visibleComments.length} {visibleComments.length === 1 ? 'kommentar' : 'kommentarer'})
          </span>
          {userRole === 'admin' && internalCount > 0 && (
            <span className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
              <Lock className="h-3 w-3" />
              {internalCount} interna
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {!isCollapsed && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation()
                refetch()
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  e.stopPropagation()
                  refetch()
                }
              }}
              className="p-1 text-nature-500 hover:text-nature-700 dark:hover:text-nature-300 transition-colors cursor-pointer"
              title="Uppdatera"
            >
              <RefreshCw className="h-4 w-4" />
            </span>
          )}
          {isCollapsed ? (
            <ChevronDown className="h-4 w-4 text-nature-400" />
          ) : (
            <ChevronUp className="h-4 w-4 text-nature-400" />
          )}
        </div>
      </button>

      {/* Content */}
      {!isCollapsed && (
        <div className={isSubsection ? 'mt-2' : 'px-6 pb-6'}>
          {/* Filter for admins */}
          {userRole === 'admin' && internalCount > 0 && (
            <div className="flex items-center gap-2 mb-4 pb-4 border-b border-nature-200 dark:border-nature-700">
              <span className="text-sm text-nature-600 dark:text-nature-400">Visa:</span>
              <button
                onClick={() => setShowInternalOnly(false)}
                className={`
                  px-3 py-1 text-sm rounded-full transition-colors
                  ${!showInternalOnly
                    ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                    : 'text-nature-600 dark:text-nature-400 hover:bg-nature-100 dark:hover:bg-nature-700'
                  }
                `}
              >
                Alla ({comments.length})
              </button>
              <button
                onClick={() => setShowInternalOnly(true)}
                className={`
                  flex items-center gap-1 px-3 py-1 text-sm rounded-full transition-colors
                  ${showInternalOnly
                    ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                    : 'text-nature-600 dark:text-nature-400 hover:bg-nature-100 dark:hover:bg-nature-700'
                  }
                `}
              >
                <Lock className="h-3 w-3" />
                Endast interna ({internalCount})
              </button>
            </div>
          )}

          {/* Comment form */}
          <CommentForm
            userRole={userRole}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            assignmentId={entityType === 'assignment' ? entityId : undefined}
          />

          {/* Comments list */}
          <div className="mt-6 space-y-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-nature-500" />
              </div>
            ) : filteredComments.length === 0 ? (
              <div className="text-center py-8 text-nature-500">
                {showInternalOnly
                  ? 'Inga interna kommentarer ännu'
                  : `Inga kommentarer på ${ENTITY_LABELS[entityType]} ännu. Bli först att kommentera!`
                }
              </div>
            ) : (
              filteredComments.map(comment => (
                <CommentItem
                  key={comment.id}
                  comment={comment}
                  currentUserRole={userRole}
                  currentUserId={userId}
                  onUpdate={handleUpdate}
                  onDelete={handleDelete}
                  onDeleteAttachment={handleDeleteAttachment}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
